import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Paper,
  useTheme,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { 
  FlightTakeoff as FlightIcon,
  ArrowBack as ArrowBackIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { THEME_GRADIENTS, THEME_SHADOWS, COMMON_STYLES, THEME_RADIUS } from '../constants/theme';
import api from '../config/api';
import useClientInvitePreview from '../hooks/useClientInvitePreview';
import { withClientInviteQuery } from '../utils/clientInvite';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const theme = useTheme();
  const { token: brandToken } = useClientInvitePreview();
  const loginPath = withClientInviteQuery('/login', brandToken);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        password: formData.password
      });
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate(loginPath);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: THEME_RADIUS.small,
            ...COMMON_STYLES.professionalCard
          }}
        >
          Invalid or missing reset token. Please request a new password reset link.
        </Alert>
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link
            component={RouterLink}
            to={withClientInviteQuery('/forgot-password', brandToken)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              color: theme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            <ArrowBackIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Back to Forgot Password
          </Link>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Hero Header Section */}
      <Box
        sx={{
          background: THEME_GRADIENTS.primary,
          color: 'white',
          pt: { xs: 6, md: 8 },
          pb: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.08"%3E%3Ccircle cx="30" cy="30" r="1.5"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.6
          }
        }}
      >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Box sx={{ mb: 4 }}>
            <Chip
              label="🔐 Reset Password"
              sx={{
                ...COMMON_STYLES.glassMorphism,
                color: 'white',
                mb: 2,
                fontWeight: 600,
                fontSize: '1rem',
                py: 1.5,
                borderRadius: THEME_RADIUS.small,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 800,
                mb: 2,
                lineHeight: 1.1,
                color: 'white',
                textShadow: '0 4px 8px rgba(0,0,0,0.1)',
              }}
            >
              Create New Password
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.95,
                mb: 4,
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                fontWeight: 400,
                lineHeight: 1.4,
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              Please enter your new password below
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Form Section */}
      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>
        <Paper 
          elevation={0}
          sx={{ 
            ...COMMON_STYLES.professionalCard,
            p: { xs: 3, md: 4 },
            mt: { xs: -4, md: -6 },
            position: 'relative',
            zIndex: 2,
            boxShadow: THEME_SHADOWS.heavy,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 4,
                background: THEME_GRADIENTS.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                boxShadow: THEME_SHADOWS.colored,
              }}
            >
              <FlightIcon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              Reset Password
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: THEME_RADIUS.small,
                border: '1px solid rgba(244, 67, 54, 0.2)'
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3, 
                borderRadius: THEME_RADIUS.small,
                border: '1px solid rgba(76, 175, 80, 0.2)'
              }}
            >
              Password has been reset successfully. Redirecting to login...
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              disabled={loading || success}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mt: 1,
                ...COMMON_STYLES.professionalInput,
              }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              disabled={loading || success}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mt: 2,
                ...COMMON_STYLES.professionalInput,
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || success || !formData.password || !formData.confirmPassword}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: THEME_RADIUS.small,
                background: THEME_GRADIENTS.primary,
                boxShadow: THEME_SHADOWS.medium,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: THEME_SHADOWS.heavy,
                },
                '&:disabled': {
                  background: theme.palette.grey[300],
                  color: theme.palette.grey[500],
                }
              }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                component={RouterLink}
                to={loginPath}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
              >
                <ArrowBackIcon sx={{ mr: 0.5, fontSize: 20 }} />
                Back to Login
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPassword; 